import { useState } from 'react';
import { FileText, Download, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useChargebackStore } from '../../stores/chargebackStore';
import { devLog } from '../../utils/formatters';

export default function Step3_Review({ onBack, onComplete }) {
  const {
    selectedOem,
    periodStart,
    periodEnd,
    selectedPartIds,
    eligibleParts,
    getTotalAmount,
    getTotalValue,
    createChargeback,
    generatePdf,
    generating
  } = useChargebackStore();

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const selectedParts = eligibleParts.filter(part => selectedPartIds.includes(part.id));

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleGeneratePdf = async () => {
    setError(null);
    setCreating(true);

    try {
      devLog('Creating chargeback...');
      
      // Create chargeback first
      const chargeback = await createChargeback();
      devLog('Chargeback created:', chargeback);
      
      if (!chargeback || !chargeback.chargeback_id) {
        throw new Error('Failed to create chargeback. No ID returned.');
      }
      
      devLog('Generating PDF...');
      
      // Generate PDF
      const pdf = await generatePdf();
      devLog('PDF response:', pdf);
      
      if (!pdf) {
        throw new Error('No response from PDF generation');
      }
      
      if (!pdf.pdf_url) {
        throw new Error('PDF URL not returned from server');
      }
      
      // Store PDF data
      setPdfData(pdf);
      
      // Trigger actual file download
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const downloadUrl = `${backendUrl}${pdf.pdf_url}`;
      
      devLog('Downloading PDF from:', downloadUrl);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${pdf.chargeback_number}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess(true);
      setCreating(false);
      
      devLog('PDF generation complete!');
    } catch (err) {
      console.error('PDF generation error:', err);
      setError(err.message || 'Failed to generate chargeback');
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Step 3: Review & Generate</CardTitle>
          <CardDescription>
            Review the chargeback details before generating the PDF
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary Card */}
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">OEM / Customer</p>
                <p className="text-lg font-semibold">{selectedOem}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Time Period</p>
                <p className="text-lg font-semibold">
                  {formatDate(periodStart)} - {formatDate(periodEnd)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Selected Parts</p>
                <p className="text-lg font-semibold">{selectedPartIds.length} parts</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Part Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(getTotalValue())}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Tariff Exposure</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(getTotalAmount())}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Effective Rate</p>
                <p className="text-lg font-semibold">
                  {((getTotalAmount() / getTotalValue()) * 100).toFixed(2)}%
                </p>
              </div>
            </div>
          </div>

          {/* Parts List Preview */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-3">Parts Included ({selectedParts.length})</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {selectedParts.slice(0, 10).map((part) => (
                <div key={part.id} className="flex justify-between text-sm">
                  <span className="text-gray-700">{part.part_number} - {part.description}</span>
                  <span className="font-medium">{formatCurrency(part.tariff_cents)}</span>
                </div>
              ))}
              {selectedParts.length > 10 && (
                <p className="text-xs text-gray-500 pt-2">
                  + {selectedParts.length - 10} more parts...
                </p>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Message */}
          {success && pdfData && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <div className="flex items-center justify-between">
                  <span>
                    <strong>PDF Generated Successfully!</strong> 
                    <br />
                    Chargeback {pdfData.chargeback_number} - {(pdfData.file_size_bytes / 1024).toFixed(1)} KB
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-4"
                    onClick={() => {
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                      const link = document.createElement('a');
                      link.href = `${backendUrl}${pdfData.pdf_url}`;
                      link.download = `${pdfData.chargeback_number}.pdf`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download Again
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={onBack} disabled={creating || success}>
              ← Back to Part Selection
            </Button>

            <div className="flex gap-2">
              {success ? (
                <Button
                  onClick={onComplete}
                  size="lg"
                  className="min-w-[200px]"
                >
                  Return to Dashboard
                </Button>
              ) : (
                <Button
                  onClick={handleGeneratePdf}
                  disabled={creating}
                  size="lg"
                  className="min-w-[200px]"
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Generate & Download PDF
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>💡 Next Steps:</strong> Once generated, the PDF chargeback will be 
              submitted to {selectedOem}. You can track the status in the Chargebacks section.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
